import axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import { AnketaResponse, ApiMethods, AnketaRequest } from "./types";
import { AnketaApiResponse, SERVER_URL } from "./functions";

export const url = `${SERVER_URL}api/`;

export const useApi = () => {
  const makeRequest = async <T,>(
    method: ApiMethods,
    { path, token, value, responseType }: AnketaRequest,
  ): Promise<AnketaResponse<T>> => {
    const config: AxiosRequestConfig = {
      responseType: responseType ?? "json",
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    };

    try {
      let response;

      switch (method) {
        case "get":
        case "delete":
          response = await axios[method](url + path, config);
          break;

        case "post":
        case "put":
          response = await axios[method](url + path, value, config);
          break;

        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      return AnketaApiResponse({
        status: response.status,
        data: response.data as T,
      });
    } catch (error: unknown) {
      // Forward the AxiosError so the response carries the real error (message
      // + the backend's response body in `error.service`). Previously this
      // returned `{ status, data: null }`, which AnketaApiResponse treats as a
      // *success* with empty data - hiding every backend error message.
      return AnketaApiResponse<T>(error as AxiosError);
    }
  };

  const Get = async <T,>(params: AnketaRequest): Promise<AnketaResponse<T>> => {
    return makeRequest("get", params);
  };

  const Post = async <T,>(
    params: AnketaRequest,
  ): Promise<AnketaResponse<T>> => {
    return makeRequest("post", params);
  };

  const Put = async <T,>(params: AnketaRequest): Promise<AnketaResponse<T>> => {
    return makeRequest("put", params);
  };

  const Delete = async <T,>(
    params: AnketaRequest,
  ): Promise<AnketaResponse<T>> => {
    return makeRequest("delete", params);
  };

  return { Get, Post, Put, Delete };
};
